import React, { useEffect, useId, useState } from 'react';
import './ContractorForm.css';

/**
 * ONE FORM FOR A CONTRACTOR, wherever one is added or edited.
 *
 * There were three: Settings had all eleven fields, Ticket Mgmt's "Add
 * contractor" had five of them, and the two had drifted - a contractor added
 * from the hub had no billing rate, no availability and no skills until
 * somebody opened their profile and did it again. Settings -> Contractors and
 * Ticket Mgmt -> Contractors both draw this one now.
 *
 *   initial    the contractor being edited, or nothing for a new one
 *   onSubmit   async (values) => void; throw an Error to keep the form open
 *              with its message under it
 *   onCancel   shows a Cancel button when given
 *   submitLabel, title
 *
 * A row from the server carries nulls for anything unset; they are turned
 * into the form's own empty values here, because a null in a controlled
 * input makes React treat it as uncontrolled from then on - it stops
 * accepting what you type, with only a console warning to say why.
 */
export const EMPTY_CONTRACTOR = {
  name: '', email: '', phone: '', company: '', skills: '',
  hourly_rate: '', billing_rate: '', availability_status: 'available',
  max_concurrent_tickets: '', is_certified: false, notes: ''
};

export const AVAILABILITY = [
  ['available', 'Available'],
  ['busy', 'Busy'],
  ['unavailable', 'Unavailable'],
  ['on-leave', 'On leave'],
  ['inactive', 'Inactive']
];

export function formOf(c) {
  if (!c) return { ...EMPTY_CONTRACTOR };
  return {
    name: c.name ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    company: c.company ?? '',
    skills: c.skills ?? '',
    hourly_rate: c.hourly_rate ?? '',
    billing_rate: c.billing_rate ?? '',
    availability_status: c.availability_status || 'available',
    max_concurrent_tickets: c.max_concurrent_tickets ?? '',
    is_certified: c.is_certified === true,
    notes: c.notes ?? ''
  };
}

export default function ContractorForm({ initial = null, onSubmit, onCancel = null,
  submitLabel = null, title = null, id = undefined }) {
  const uid = useId();
  const [form, setForm] = useState(() => formOf(initial));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  // A different contractor to edit (or none) starts the form again.
  useEffect(() => { setForm(formOf(initial)); setProblem(null); }, [initial]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const editing = Boolean(initial);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setProblem('A name and an email address are both needed.');
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await onSubmit({ ...form, name: form.name.trim(), email: form.email.trim() });
      if (!editing) setForm({ ...EMPTY_CONTRACTOR });
    } catch (err) {
      setProblem(err?.message || 'Could not save the contractor.');
    } finally {
      setBusy(false);
    }
  };

  const f = (key) => `${uid}-${key}`;
  return (
    <form className="cfm" onSubmit={submit} id={id} noValidate>
      {title && <h3 className="cfm-title">{title}</h3>}
      <div className="cfm-grid">
        <div className="cfm-field">
          <label htmlFor={f('name')}>Name <span className="cfm-req" aria-hidden="true">*</span></label>
          <input id={f('name')} type="text" value={form.name} onChange={set('name')} required autoComplete="off" />
        </div>
        <div className="cfm-field">
          <label htmlFor={f('email')}>Email <span className="cfm-req" aria-hidden="true">*</span></label>
          <input id={f('email')} type="email" value={form.email} onChange={set('email')} required autoComplete="off" />
        </div>
        <div className="cfm-field">
          <label htmlFor={f('phone')}>Phone</label>
          <input id={f('phone')} type="tel" value={form.phone} onChange={set('phone')} />
        </div>
        <div className="cfm-field">
          <label htmlFor={f('company')}>Company</label>
          <input id={f('company')} type="text" value={form.company} onChange={set('company')} />
        </div>
        <div className="cfm-field cfm-wide">
          <label htmlFor={f('skills')}>Skills</label>
          <input id={f('skills')} type="text" value={form.skills} onChange={set('skills')}
                 placeholder="e.g. Hardware repair, network administration" />
        </div>
        <div className="cfm-field">
          <label htmlFor={f('hourly')}>Hourly rate (€)</label>
          <input id={f('hourly')} type="number" min="0" step="0.01" value={form.hourly_rate} onChange={set('hourly_rate')}
                 aria-describedby={`${f('hourly')}-hint`} />
          <small id={`${f('hourly')}-hint`}>What we pay them.</small>
        </div>
        <div className="cfm-field">
          <label htmlFor={f('billing')}>Billing rate (€)</label>
          <input id={f('billing')} type="number" min="0" step="0.01" value={form.billing_rate} onChange={set('billing_rate')}
                 aria-describedby={`${f('billing')}-hint`} />
          <small id={`${f('billing')}-hint`}>What the client is charged.</small>
        </div>
        <div className="cfm-field">
          <label htmlFor={f('avail')}>Availability</label>
          <select id={f('avail')} value={form.availability_status} onChange={set('availability_status')}
                  aria-describedby={`${f('avail')}-hint`}>
            {AVAILABILITY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <small id={`${f('avail')}-hint`}>Only Available and Busy are offered when assigning work.</small>
        </div>
        <div className="cfm-field">
          <label htmlFor={f('max')}>Most tickets at once</label>
          <input id={f('max')} type="number" min="1" step="1" placeholder="5" value={form.max_concurrent_tickets}
                 onChange={set('max_concurrent_tickets')} />
        </div>
        <div className="cfm-field cfm-check">
          <label htmlFor={f('cert')}>
            <input id={f('cert')} type="checkbox" checked={form.is_certified} onChange={set('is_certified')} />
            <span>Certified</span>
          </label>
        </div>
        <div className="cfm-field cfm-wide">
          <label htmlFor={f('notes')}>Notes</label>
          <textarea id={f('notes')} rows={2} value={form.notes} onChange={set('notes')}
                    aria-describedby={`${f('notes')}-hint`} />
          <small id={`${f('notes')}-hint`}>Never shown to the contractor.</small>
        </div>
      </div>
      {problem && <p className="cfm-problem" role="alert">{problem}</p>}
      <div className="cfm-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : (submitLabel || (editing ? 'Save changes' : 'Add contractor'))}
        </button>
        {onCancel && (
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
        )}
      </div>
    </form>
  );
}
